import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLoadScript, GoogleMap, Marker } from '@react-google-maps/api';
import './VendorRegistration.css'; // Reuse the same styles

const libraries = ['places'];

const CourierSubscriptionForm = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    dispensaryName: '',
    dispensaryType: 'Delivery', // Fixed value for courier service
    license: '',
    phone: '' // Added phone field
  });

  const [address, setAddress] = useState('');
  const [coordinates, setCoordinates] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 34.0522, lng: -118.2437 }); // Los Angeles default

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  useEffect(() => {
    if (isLoaded && window.google) {
      const autocomplete = new window.google.maps.places.Autocomplete(
        document.getElementById('address-input'),
        { types: ['address'], componentRestrictions: { country: 'us' } }
      );

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.geometry && place.geometry.location) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          
          // Ensure coordinates are numbers and in the correct format
          const validLng = parseFloat(lng.toFixed(7));
          const validLat = parseFloat(lat.toFixed(7));
          
          setCoordinates([validLng, validLat]); // MongoDB uses [longitude, latitude] format
          setMapCenter({ lat: validLat, lng: validLng });
          setAddress(place.formatted_address);
          
          console.log('Set coordinates:', [validLng, validLat]);
          console.log('Set formatted address:', place.formatted_address);
        }
      });
    }
  }, [isLoaded]);

  // Phone number formatting function
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const cleaned = ('' + value).replace(/\D/g, '');
    
    // Limit to 10 digits
    const limited = cleaned.slice(0, 10);
    
    // Check if the input is of correct length
    const match = limited.match(/^(\d{3})(\d{3})(\d{4})$/);
    
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    
    // Handle partial input
    if (limited.length > 6) {
      return '(' + limited.slice(0,3) + ') ' + limited.slice(3,6) + '-' + limited.slice(6);
    } else if (limited.length > 3) {
      return '(' + limited.slice(0,3) + ') ' + limited.slice(3);
    } else if (limited.length > 0) {
      return '(' + limited;
    }
    
    return '';
  };

  // Handle phone number change
  const handlePhoneChange = (e) => {
    const input = e.target.value;
    const formatted = formatPhoneNumber(input);
    setFormData({...formData, phone: formatted});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Basic validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // All vendors need address and coordinates
    if (!coordinates || !Array.isArray(coordinates) || coordinates.length !== 2) {
      setError('Valid address with coordinates is required');
      setLoading(false);
      return;
    }
    
    if (!address) {
      setError('Formatted address is required');
      setLoading(false);
      return;
    }
    
    // Validate coordinates format
    if (coordinates && (!Array.isArray(coordinates) || coordinates.length !== 2 || 
        typeof coordinates[0] !== 'number' || typeof coordinates[1] !== 'number')) {
      setError('Invalid coordinate format');
      setLoading(false);
      return;
    }

    try {
      // Format registration data with courier service enabled
      const registrationData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        dispensaryName: formData.dispensaryName,
        dispensaryType: 'Delivery', // Fixed for courier service
        license: formData.license,
        phone: formData.phone,
        deliveryZone: {
          type: 'Point',
          coordinates: coordinates
        },
        // Enable courier service with subscription plan
        courierService: {
          enabled: true,
          plan: 'subscription', // Always subscription for this form
          status: 'pending',
          signupDate: new Date(),
          notes: 'Courier service subscription signup'
        },
        // Include contact numbers if phone is provided
        contactNumbers: formData.phone ? {
          primary: {
            formatted: formData.phone,
            e164: formData.phone.replace(/\D/g, '') // Just the digits
          }
        } : {}
      };

      // Log the data being sent
      console.log('Sending courier subscription registration data:', JSON.stringify(registrationData, null, 2));

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData),
        credentials: 'include'
      });

      const data = await response.json();

      console.log('Server response:', data);

      if (data.success) {
        navigate('/login');
      } else {
        setError(data.message || 'Registration failed');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'An error occurred during registration');
    } finally {
      setLoading(false);
    }
  };

  if (loadError) return <div className="text-center mt-4">Error loading maps</div>;
  if (!isLoaded) return <div className="text-center mt-4">Loading maps...</div>;

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-md-8 col-lg-6">
          <div className="card shadow-sm">
            <div className="card-body p-4">
              <h2 className="card-title text-center mb-4">WeedHurry Courier Subscription</h2>
              
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">First Name</label>
                    <input
                      type="text"
                      className="form-control"
                      required
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Last Name</label>
                    <input
                      type="text"
                      className="form-control"
                      required
                      value={formData.lastName}
                      onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Phone Number</label>
                  <input
                    type="tel"
                    className="form-control"
                    required
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    placeholder="(123) 456-7890"
                  />
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Password</label>
                    <input
                      type="password"
                      className="form-control"
                      required
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Confirm Password</label>
                    <input
                      type="password"
                      className="form-control"
                      required
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Dispensary Name</label>
                  <input
                    type="text"
                    className="form-control"
                    required
                    value={formData.dispensaryName}
                    onChange={(e) => setFormData({...formData, dispensaryName: e.target.value})}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">License Number</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.license}
                    onChange={(e) => setFormData({...formData, license: e.target.value})}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Dispensary Address</label>
                  <input
                    id="address-input"
                    className="form-control"
                    placeholder="Enter and select from dropdown"
                    required={true}
                  />
                </div>

                {coordinates && (
                  <div className="mb-3" style={{ height: '300px', width: '100%' }}>
                    <GoogleMap
                      zoom={15}
                      center={mapCenter}
                      mapContainerStyle={{ width: '100%', height: '100%' }}
                    >
                      <Marker position={mapCenter} />
                    </GoogleMap>
                  </div>
                )}

                <div className="alert alert-info mb-3">
                  <h5>WeedHurry Courier Subscription Plan</h5>
                  <p className="mb-0">$79/month + 15% commission on each delivery</p>
                  <small className="text-muted">Start your 30-day free trial today!</small>
                </div>

                <button 
                  type="submit" 
                  className="btn btn-primary w-100"
                  disabled={loading}
                >
                  {loading ? 'Subscribing...' : 'Subscribe to Courier Service'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourierSubscriptionForm;